import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../../../styles/FlightRiskAssessment.css";
import ClientService from "../../../services/clients/ClientService";
import UserInteractionsService from "../../../services/interactions/Interactions";
import { capitalizeFirstLetter, extractNameFromEmail, getAvatarInitials } from "../../../utils/utils";
import RichTextEditor from "../../Common/RichTextEditor";
import CustomDropdown from "../../Common/CustomDropdown";
import {
  FlightRiskAssessmentProps,
  FlightRiskFormErrors,
  FlightRiskFormState,
  FlightRiskQuestions,
} from "../../../interfaces/flightRisk";
import {
  parseRatingScore,
  getUrgencyStyleClasses,
  getConfidenceStyleClasses,
  displayWalmartAM,
  displayMetricNumber,
  formatDateWithSlash,
  EMPTY_FORM,
  apiToForm,
  validateScore,
  formsAreEqual,
  formToFlightRiskPayload,
} from "../../../utils/flightRiskUtils";
import GreenCheckBoxIcon from "../../../assets/common/green_check_box_icon.svg";
import RedCrossBoxIcon from "../../../assets/common/red_cross_box_icon.svg";
import useDisplaySnackbar from "../../../utils/useDisplaySnackbar";
import {
  Avatar,
  CircularProgress,
  ClickAwayListener,
  Paper,
  Popper,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import useGlobalContext from "../../../context/useGlobalContext";
import EditIcon from "../../../assets/common/edit-icon.svg";
import CommonDatePicker from "../../Common/CommonDatePicker";
import dayjs from "dayjs";
import DueDateIcon from "../../../assets/action-items/due-date-calendar.svg";

// ─── Component ─────────────────────────────────────────────────────────────────

const FlightRiskAssessment: React.FC<FlightRiskAssessmentProps> = ({
  orgId,
  subOrgId,
  setFlightRiskTimestamp,
  isCreateMode,
  accountManagerName: propAccountManagerName,
  isFlightRiskCreateUpdateEnabled,
  onHeaderActionsChange,
  onEditModeChange,
  onClose,
  onCreateSuccess,
}) => {
  const { featureMap } = useGlobalContext();
  const isFlightRiskEnabled =
    featureMap?.accounts_customers_flight_risk === true;
  const isFlightRiskQueryEnabled =
    isFlightRiskEnabled &&
    Boolean(orgId) &&
    Boolean(subOrgId) &&
    !isCreateMode;
  const { showSnackbar } = useDisplaySnackbar();

  const {
    data: flightRiskData,
    isLoading: isFlightRiskLoading,
    isError: isFlightRiskError,
    error: flighRiskError,
  } = ClientService.useGetFlightRiskForClient(
    { orgId: orgId ?? "", subOrgId: subOrgId ?? "" },
    isFlightRiskQueryEnabled,
  );


  // ── Edit mode state ─────────────────────────────────────────────────────────
  const queryClient = useQueryClient();
  const [isEditMode, setIsEditMode] = useState(Boolean(isCreateMode));
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<FlightRiskFormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FlightRiskFormErrors>({});

  // ── Active users for autocomplete in top metadata fields ───────────────────
  const { data: activeUsersData } = UserInteractionsService.useGetActiveUsers(
    {},
    Boolean(isEditMode || isCreateMode),
  );

  const activeUsers = useMemo(() => {
    const raw = activeUsersData?.data?.data ?? [];
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    return raw
      .filter((u: any) => {
        if (!u?.email) return false;
        const normalized = u.email.trim().toLowerCase();
        if (seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
      })
      .map((u: any) => ({
        name: capitalizeFirstLetter(u.name) || extractNameFromEmail("", u.email),
        email: u.email.trim().toLowerCase(),
        avatar_url: u.pictureUrl,
      }));
  }, [activeUsersData]);

  // Memoized fast lookup maps by email
  const userByEmailMap = useMemo(() => {
    const map = new Map<string, (typeof activeUsers)[0]>();
    activeUsers.forEach((u) => {
      map.set(u.email.toLowerCase(), u);
    });
    return map;
  }, [activeUsers]);

  const getUserDisplayName = useCallback(
    (val: string | null | undefined): string => {
      if (!val) return "";
      const trimmed = val.trim();
      const user = userByEmailMap.get(trimmed.toLowerCase());
      return user ? user.name : trimmed;
    },
    [userByEmailMap]
  );

  const [activeDropdownField, setActiveDropdownField] = useState<
    "email" | "hubLeader" | "strategist" | "ppcManager" | null
  >(null);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpenUserDropdown = (
    field: "email" | "hubLeader" | "strategist" | "ppcManager",
    el: HTMLElement
  ) => {
    if (activeDropdownField === field) {
      setActiveDropdownField(null);
      setDropdownAnchorEl(null);
    } else {
      setActiveDropdownField(field);
      setDropdownAnchorEl(el);
    }
  };

  const handleCloseUserDropdown = useCallback(() => {
    setActiveDropdownField(null);
    setDropdownAnchorEl(null);
  }, []);

  const handleSelectUser = useCallback(
    (selected: any) => {
      if (!activeDropdownField) return;
      const emailVal = selected?.email ? selected.email.trim() : "";
      handleChange(activeDropdownField, emailVal);
      handleCloseUserDropdown();
    },
    [activeDropdownField, handleCloseUserDropdown]
  );

  const selectedDropdownUser = useMemo(() => {
    if (!activeDropdownField) return null;
    const currentVal = form[activeDropdownField]?.trim().toLowerCase();
    if (!currentVal) return null;
    return userByEmailMap.get(currentVal) || null;
  }, [activeDropdownField, form, userByEmailMap]);

  const effectiveMinDate = useMemo(() => {
    const defaultMin = dayjs().subtract(1, "year").startOf("day");
    if (form.dateOfLastInteraction && dayjs(form.dateOfLastInteraction).isBefore(defaultMin)) {
      return dayjs(form.dateOfLastInteraction).startOf("day").toISOString();
    }
    return defaultMin.toISOString();
  }, [form.dateOfLastInteraction]);
  const renderDropdownUserItem = useCallback((item: any) => {
    return (
      <div className="fr-dropdown-user-row">
        <Avatar
          src={item.avatar_url || item.pictureUrl}
          alt={item.name || item.email}
          className="fr-dropdown-user-avatar"
        >
          {!item.avatar_url && getAvatarInitials(item.name, item.email)}
        </Avatar>
        <div className="fr-dropdown-user-info">
          <span className="fr-dropdown-user-name">
            {item.name || item.email}
          </span>
          {item.name && item.email && (
            <span className="fr-dropdown-user-email">
              {item.email}
            </span>
          )}
        </div>
      </div>
    );
  }, []);

  // Snapshot of the form from the last successful API load — used for dirty detection
  const initialFormRef = useRef<FlightRiskFormState>(EMPTY_FORM);

  // Notify parent slideover when entering/leaving edit or create mode (to hide/show X button)
  useEffect(() => {
    onEditModeChange?.(isEditMode || Boolean(isCreateMode));
  }, [isEditMode, isCreateMode, onEditModeChange]);

  // ── Populate form from API data ─────────────────────────────────────────────
  useEffect(() => {
    if (flightRiskData && !isCreateMode && !isEditMode) {
      const populated = apiToForm(flightRiskData);
      setForm(populated);
      initialFormRef.current = populated;
    }
  }, [flightRiskData, isCreateMode, isEditMode]);

  useEffect(() => {
    if (isCreateMode) {
      const d = new Date();
      setFlightRiskTimestamp?.(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);
    } else if (flightRiskData?.timeStamp) {
      setFlightRiskTimestamp?.(flightRiskData.timeStamp);
    }
  }, [isCreateMode, flightRiskData?.timeStamp, setFlightRiskTimestamp]);

  useEffect(() => {
    if (isFlightRiskError) {
      showSnackbar(
        "error",
        flighRiskError?.message || "Failed to load insights",
      );
    }
  }, [flighRiskError]);

  // ── Derived state ───────────────────────────────────────────────────────────
  const isDirty = !formsAreEqual(form, initialFormRef.current);
  const hasErrors =
    Boolean(formErrors.urgency) ||
    Boolean(formErrors.confidence);
  const hasAtLeastOneField = Object.values(form).some(
    (val) => typeof val === "string" && val.trim() !== "",
  );
  const canSubmit = isCreateMode
    ? hasAtLeastOneField && !hasErrors && !isSaving
    : isDirty && !hasErrors && !isSaving;

  // ── Field change handler ────────────────────────────────────────────────────
  const handleChange = (field: keyof FlightRiskFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));

    if (field === "urgency") {
      setFormErrors((prev) => ({ ...prev, urgency: validateScore(value) }));
    }
    if (field === "confidence") {
      setFormErrors((prev) => ({ ...prev, confidence: validateScore(value) }));
    }
  };

  // ── Edit / Discard / Save ───────────────────────────────────────────────────
  const handleEdit = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const handleDiscard = useCallback(() => {
    setDatePickerAnchorEl(null);
    if (isCreateMode) {
      setForm(EMPTY_FORM);
      setFormErrors({});
      onClose?.();
    } else {
      setForm(initialFormRef.current);
      setFormErrors({});
      setIsEditMode(false);
    }
  }, [isCreateMode, onClose]);

  
const handleSave = useCallback(async () => {
  // 1. Validate the form before creating the payload.
  const newErrors: FlightRiskFormErrors = {
    urgency: validateScore(form.urgency),
    confidence: validateScore(form.confidence),
  };

  const hasNewErrors = Object.values(newErrors).some(Boolean);

  if (hasNewErrors) {
    setFormErrors(newErrors);
    return;
  }

  // 2. Validate required organization information.
  if (!orgId || !subOrgId) {
    showSnackbar("error", "Organization information missing");
    return;
  }

  // 3. Build the payload containing only changed fields.
  const payload = formToFlightRiskPayload(
    form,
    initialFormRef.current,
    orgId,
    subOrgId,
    isCreateMode,
    isCreateMode
      ? "Flagging as a Flight Risk"
      : flightRiskData?.flightRiskStatus || "Flagging as a Flight Risk",
  );

  const updatedFields = Object.keys(payload.fieldValues);

  // 4. In create mode require at least one field; in edit mode skip if nothing changed.
  if (isCreateMode && !hasAtLeastOneField) {
    return;
  }
  if (!isCreateMode && updatedFields.length === 0) {
    setIsEditMode(false);
    return;
  }

  setIsSaving(true);

  try {
    // 5. Send only the changed fields to the backend.
    await ClientService.updateFlightRiskFields(payload);

    showSnackbar(
      "success",
      isCreateMode
        ? "Flight risk assessment created successfully"
        : "Flight risk assessment updated successfully",
    );

    // 6. Invalidate relevant queries after a successful save.
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["get_flight_risk_for_client"],
      }),
      queryClient.invalidateQueries({ queryKey: ["get_org_summary"] }),
      queryClient.invalidateQueries({ queryKey: ["get_org_summary_space_mode"] }),
    ]);

    // 7. Handle create and update flows separately.
    if (isCreateMode) {
      // Stay in the slideover — let the parent switch to view mode so the
      // freshly-created record loads via the invalidated query above.
      onCreateSuccess?.();
      return;
    }

    // 8. Update the initial snapshot only after a successful update.
    initialFormRef.current = { ...form };

    setIsEditMode(false);
    setFormErrors({});
  } catch (err: any) {
    showSnackbar(
      "error",
      err?.response?.data?.message ||
        err?.message ||
        "Failed to save flight risk assessment",
    );
  } finally {
    setIsSaving(false);
  }
}, [
  form,
  orgId,
  subOrgId,
  isCreateMode,
  flightRiskData?.flightRiskStatus,
  showSnackbar,
  queryClient,
  onCreateSuccess,
]);

 // Date picker handlers
  const [datePickerAnchorEl, setDatePickerAnchorEl] = useState<HTMLElement | null>(null);
  const isDatePickerOpen = Boolean(datePickerAnchorEl);

  const handleOpenDatePicker = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setDatePickerAnchorEl(e.currentTarget);
  };

  const handleCloseDatePicker = useCallback(() => {
    setDatePickerAnchorEl(null);
  }, []);

  const handleDateChange = (date: string | null) => {
    const formatted = date
      ? dayjs(date).isValid()
        ? dayjs(date).format("YYYY-MM-DD")
        : date
      : "";
    handleChange("dateOfLastInteraction", formatted);
    setDatePickerAnchorEl(null);
  };
  // ── Inject header action buttons via callback ───────────────────────────────
  useEffect(() => {
    if (!onHeaderActionsChange) return;

    if (isEditMode || isCreateMode) {
      const actions = (
        <>
          <button
            type="button"
            className="fr-header-btn fr-header-btn--discard"
            onClick={handleDiscard}
            disabled={isSaving}
          >
            Discard
          </button>
          <button
            type="button"
            className={`fr-header-btn fr-header-btn--submit${canSubmit ? "" : " fr-header-btn--submit-disabled"}`}
            onClick={handleSave}
            disabled={!canSubmit}
          >
            <span>✓</span>
            {isSaving ? " Saving..." : (isCreateMode ? " Create" : " Update")}
          </button>
        </>
      );
      onHeaderActionsChange(actions);
    } else if (
      !isFlightRiskError &&
      !isFlightRiskLoading &&
      flightRiskData &&
      isFlightRiskCreateUpdateEnabled
    ) {
      const actions = (
        <button
          type="button"
          className="fr-header-btn fr-header-btn--edit"
          onClick={handleEdit}
        >
          <img src={EditIcon} className="fr-header-icon" alt="" /> Edit Data
        </button>
      );
      onHeaderActionsChange(actions);
    } else {
      onHeaderActionsChange(null);
    }
  }, [
    isEditMode,
    isCreateMode,
    canSubmit,
    isSaving,
    isFlightRiskError,
    isFlightRiskLoading,
    flightRiskData,
    isFlightRiskCreateUpdateEnabled,
    handleDiscard,
    handleSave,
    handleEdit,
    onHeaderActionsChange,
  ]);

  // ── Loading / Error guard (only for view mode with existing data) ───────────
  if (!isCreateMode) {
    if (isFlightRiskLoading && !flightRiskData) {
      return (
        <div className="flight-risk-state-loading">
          <CircularProgress
            size={24}
            thickness={5}
            aria-label="Loading flight risk assessment"
          />
        </div>
      );
    }

    if (isFlightRiskError) {
      return (
        <div className="flight-risk-state flight-risk-state--error">
          Failed to load flight Risk data.
        </div>
      );
    }

    if (!flightRiskData) return null;
  }

  const urgencyRating = parseRatingScore(
    flightRiskData?.levelOfUrgencyForThisFlightRisk ?? "",
  );
  const urgencyStyles =
    urgencyRating.score !== null
      ? getUrgencyStyleClasses(urgencyRating.score)
      : null;

  const confidenceRating = parseRatingScore(
    flightRiskData?.onAScaleOf1To5HowConfidentAreYouInRetainingThisClientWithTheCurrentPlanOfActionPoa ??
      "",
  );
  const confidenceStyles =
    confidenceRating.score !== null
      ? getConfidenceStyleClasses(confidenceRating.score)
      : null;

  const hasPlanOfActionRaw = flightRiskData?.isAPlanOfActionPoaInPlace?.trim();
  const hasPlanOfAction = hasPlanOfActionRaw?.toLowerCase();
  const isPoaYesOrNo = hasPlanOfAction === "yes" || hasPlanOfAction === "no";

  const walmartAccountManager = displayWalmartAM(
    flightRiskData?.doesTheAccountHaveAWalmartAmOrConnectPartner ?? "",
  );

  // POA recap: detect whether backend data is HTML or plain text
  const poaRecapRaw =
    flightRiskData?.recapOfPlanOfActionPoaDetailsAndNextStepsIfApplicable?.trim() || "";
  const isHtmlPoaRecap = /<[a-z][\s\S]*>/i.test(poaRecapRaw);
  const planOfActionSteps =
    !isHtmlPoaRecap && poaRecapRaw
      ? poaRecapRaw
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
      : [];

  const salesMetric = displayMetricNumber(flightRiskData?.last30DaysOfSales);
  const roasMetric = displayMetricNumber(flightRiskData?.last30DaysReturnOnAdSpendRoas);

  // KPI: now an array from the backend — join for display
  const rawKpi = flightRiskData?.kpiThatBestRepresentsTheMainIssue as unknown;
  const kpiDisplay = Array.isArray(rawKpi)
    ? rawKpi
        .filter((k) => typeof k === "string" && k.trim() !== "")
        .join(", ")
    : typeof rawKpi === "string" && rawKpi.trim() !== ""
      ? rawKpi.trim()
      : null;

  // Metadata: prefer new field names, fall back to legacy, then "-"
  const email = flightRiskData?.emailAddress?.trim() || "---";
  const hubLeader =
    flightRiskData?.hubLeaderName?.trim() || "---";
  const strategist =
    flightRiskData?.strategistName?.trim() || "---";
  const ppcManager =
    flightRiskData?.ppcManagerName?.trim() || "---";
  const amFallback =
    propAccountManagerName &&
    propAccountManagerName !== null &&
    propAccountManagerName !== "" 
      ? propAccountManagerName?.trim()
      : "";
  const accountManagerName =
    flightRiskData?.accountManagerName?.trim() || amFallback || "---";

  const rawDateInteraction = flightRiskData?.dateOfLastSignificantInteraction?.trim();
  const dateOfLastSignificantInteraction = rawDateInteraction
    ? rawDateInteraction.includes("-")
      ? formatDateWithSlash(rawDateInteraction)
      : rawDateInteraction
    : "-";
  const goalsMovingForward = flightRiskData?.goalsMovingForward?.trim() || "---";
  const peopleInvolved = flightRiskData?.peopleInvolved?.trim() || "---";

  // Edit-mode score preview (real-time dots)
  const editUrgencyRating = parseRatingScore(isEditMode ? form.urgency : null);
  const editUrgencyStyles =
    editUrgencyRating.score !== null && !formErrors.urgency
      ? getUrgencyStyleClasses(editUrgencyRating.score)
      : null;

  const editConfidenceRating = parseRatingScore(isEditMode ? form.confidence : null);
  const editConfidenceStyles =
    editConfidenceRating.score !== null && !formErrors.confidence
      ? getConfidenceStyleClasses(editConfidenceRating.score)
      : null;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="flight-risk-sections">
      {/* ── Top Metadata Bar ─────────────────────────────────────────── */}
      <div className="flight-risk-top-meta-card">

        {/* AM — always read-only */}
        <div className="flight-risk-top-meta-item">
          <span className="flight-risk-top-meta-label">{FlightRiskQuestions.AM}</span>
          <span className={accountManagerName === "---" ? "flight-risk-fallback-dash" : "flight-risk-top-meta-value"}>
            {accountManagerName}
          </span>
        </div>

        {/* Email */}
        <div className="flight-risk-top-meta-item">
          <span className="flight-risk-top-meta-label">{FlightRiskQuestions.EMAIL}:</span>
          {isEditMode ? (
            <div
              className={`fr-meta-select-trigger${activeDropdownField === "email" ? " fr-meta-select-trigger--active" : ""}`}
              onClick={(e) => handleOpenUserDropdown("email", e.currentTarget)}
            >
              <span className={form.email ? "fr-meta-select-value" : "fr-meta-select-placeholder"}>
                {form.email || "Select email..."}
              </span>
              <div className="fr-meta-select-icons">
                {form.email ? (
                  <span
                    className="fr-meta-select-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChange("email", "");
                    }}
                    title="Clear"
                  >
                    ✕
                  </span>
                ) : (
                  <span className="fr-meta-select-arrow">▾</span>
                )}
              </div>
            </div>
          ) : (
            <span className={email === "---" ? "flight-risk-fallback-dash" : "flight-risk-top-meta-value"}>
              {email}
            </span>
          )}
        </div>

        {/* Hub Leader */}
        <div className="flight-risk-top-meta-item">
          <span className="flight-risk-top-meta-label">{FlightRiskQuestions.HUB_LEADER}</span>
          {isEditMode ? (
            <div
              className={`fr-meta-select-trigger${activeDropdownField === "hubLeader" ? " fr-meta-select-trigger--active" : ""}`}
              onClick={(e) => handleOpenUserDropdown("hubLeader", e.currentTarget)}
            >
              <span className={form.hubLeader ? "fr-meta-select-value" : "fr-meta-select-placeholder"}>
                {getUserDisplayName(form.hubLeader) || "Select Hub Leader..."}
              </span>
              <div className="fr-meta-select-icons">
                {form.hubLeader ? (
                  <span
                    className="fr-meta-select-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChange("hubLeader", "");
                    }}
                    title="Clear"
                  >
                    ✕
                  </span>
                ) : (
                  <span className="fr-meta-select-arrow">▾</span>
                )}
              </div>
            </div>
          ) : (
            <span className={hubLeader === "---" ? "flight-risk-fallback-dash" : "flight-risk-top-meta-value"}>
              {hubLeader}
            </span>
          )}
        </div>

        {/* Strategist */}
        <div className="flight-risk-top-meta-item">
          <span className="flight-risk-top-meta-label">{FlightRiskQuestions.STRATEGIST}:</span>
          {isEditMode ? (
            <div
              className={`fr-meta-select-trigger${activeDropdownField === "strategist" ? " fr-meta-select-trigger--active" : ""}`}
              onClick={(e) => handleOpenUserDropdown("strategist", e.currentTarget)}
            >
              <span className={form.strategist ? "fr-meta-select-value" : "fr-meta-select-placeholder"}>
                {getUserDisplayName(form.strategist) || "Select Strategist..."}
              </span>
              <div className="fr-meta-select-icons">
                {form.strategist ? (
                  <span
                    className="fr-meta-select-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChange("strategist", "");
                    }}
                    title="Clear"
                  >
                    ✕
                  </span>
                ) : (
                  <span className="fr-meta-select-arrow">▾</span>
                )}
              </div>
            </div>
          ) : (
            <span className={strategist === "---" ? "flight-risk-fallback-dash" : "flight-risk-top-meta-value"}>
              {strategist}
            </span>
          )}
        </div>

        {/* PPC Manager */}
        <div className="flight-risk-top-meta-item">
          <span className="flight-risk-top-meta-label">{FlightRiskQuestions.PPC_MANAGER}</span>
          {isEditMode ? (
            <div
              className={`fr-meta-select-trigger${activeDropdownField === "ppcManager" ? " fr-meta-select-trigger--active" : ""}`}
              onClick={(e) => handleOpenUserDropdown("ppcManager", e.currentTarget)}
            >
              <span className={form.ppcManager ? "fr-meta-select-value" : "fr-meta-select-placeholder"}>
                {getUserDisplayName(form.ppcManager) || "Select PPC Manager..."}
              </span>
              <div className="fr-meta-select-icons">
                {form.ppcManager ? (
                  <span
                    className="fr-meta-select-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChange("ppcManager", "");
                    }}
                    title="Clear"
                  >
                    ✕
                  </span>
                ) : (
                  <span className="fr-meta-select-arrow">▾</span>
                )}
              </div>
            </div>
          ) : (
            <span className={ppcManager === "---" ? "flight-risk-fallback-dash" : "flight-risk-top-meta-value"}>
              {ppcManager}
            </span>
          )}
        </div>

      </div>

      {/* ── Active User Selection Popper ──────────────────────────────── */}
      <Popper
        open={Boolean(dropdownAnchorEl && activeDropdownField)}
        anchorEl={dropdownAnchorEl}
        placement="bottom-start"
        modifiers={[
          {
            name: "flip",
            enabled: true,
            options: { fallbackPlacements: ["top-start"], padding: 8 },
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: { boundary: "viewport", padding: 8 },
          },
        ]}
        className="fr-dropdown-popper"
      >
        <ClickAwayListener onClickAway={handleCloseUserDropdown}>
          <Paper elevation={4} className="fr-dropdown-paper">
            <CustomDropdown
              items={activeUsers}
              value={selectedDropdownUser}
              onChange={handleSelectUser}
              placeholder="Search users..."
              showSearch={true}
              maxHeight={260}
              getKey={(u: any) => u.email}
              renderItem={renderDropdownUserItem}
              filterFn={(u: any, search: string) =>
                (u.name || "").toLowerCase().includes(search) ||
                (u.email || "").toLowerCase().includes(search)
              }
              onClose={handleCloseUserDropdown}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* ── Row 1: 3 metric cards ─────────────────────────────────────── */}
      <div className="flight-risk-grid-3">

        {/* Urgency */}
        <div className="flight-risk-card">
          <p className="flight-risk-card-label">{FlightRiskQuestions.URGENCY}</p>
          {isEditMode ? (
            <div className="fr-score-edit-wrap">
              <div className="fr-score-input-row">
                <input
                  type="number"
                  min={1}
                  max={5}
                  className={`fr-score-input${formErrors.urgency ? " fr-score-input--error" : ""}`}
                  value={form.urgency}
                  onChange={(e) => handleChange("urgency", e.target.value)}
                />
                <span className="fr-score-suffix">/5</span>
              </div>
              {!formErrors.urgency && form.urgency && editUrgencyRating.score !== null && editUrgencyStyles && (
                <div className="flight-risk-dot-rating fr-score-preview">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span
                      key={i}
                      className={`flight-risk-dot ${
                        i < (editUrgencyRating.score ?? 0)
                          ? editUrgencyStyles.dotClass
                          : "flight-risk-dot--empty"
                      }`}
                    />
                  ))}
                </div>
              )}
              {formErrors.urgency && (
                <span className="fr-field-error">{formErrors.urgency}</span>
              )}
            </div>
          ) : urgencyRating.rawValue === null ? (
            <span className="flight-risk-fallback-dash">---</span>
          ) : urgencyRating.isOverMax || !urgencyRating.isValidRating ? (
            <span className="flight-risk-score-plain">{urgencyRating.rawValue}</span>
          ) : (
            <div className="flight-risk-metric-row">
              <div className={`flight-risk-circle ${urgencyStyles?.circleClass ?? ""}`}>
                {urgencyRating.score}
              </div>
              <div className="flight-risk-dot-rating">
                {Array.from({ length: 5 }).map((_, dotIndex) => (
                  <span
                    key={dotIndex}
                    className={`flight-risk-dot ${
                      dotIndex < (urgencyRating.score ?? 0)
                        ? (urgencyStyles?.dotClass ?? "")
                        : "flight-risk-dot--empty"
                    }`}
                  />
                ))}
                <span className={`flight-risk-score-text ${urgencyStyles?.scoreTextClass ?? ""}`}>
                  {urgencyRating.score}/5
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Confidence */}
        <div className="flight-risk-card">
          <p className="flight-risk-card-label">{FlightRiskQuestions.CONFIDENCE}</p>
          {isEditMode ? (
            <div className="fr-score-edit-wrap">
              <div className="fr-score-input-row">
                <input
                  type="number"
                  min={1}
                  max={5}
                  className={`fr-score-input${formErrors.confidence ? " fr-score-input--error" : ""}`}
                  value={form.confidence}
                  onChange={(e) => handleChange("confidence", e.target.value)}
                />
                <span className="fr-score-suffix">/5</span>
              </div>
              {!formErrors.confidence && form.confidence && editConfidenceRating.score !== null && editConfidenceStyles && (
                <div className="flight-risk-dot-rating fr-score-preview">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span
                      key={i}
                      className={`flight-risk-dot ${
                        i < (editConfidenceRating.score ?? 0)
                          ? editConfidenceStyles.dotClass
                          : "flight-risk-dot--empty"
                      }`}
                    />
                  ))}
                </div>
              )}
              {formErrors.confidence && (
                <span className="fr-field-error">{formErrors.confidence}</span>
              )}
            </div>
          ) : confidenceRating.rawValue === null ? (
            <span className="flight-risk-fallback-dash">---</span>
          ) : confidenceRating.isOverMax || !confidenceRating.isValidRating ? (
            <span className="flight-risk-score-plain">{confidenceRating.rawValue}</span>
          ) : (
            <div className="flight-risk-metric-row">
              <div className={`flight-risk-circle ${confidenceStyles?.circleClass ?? ""}`}>
                {confidenceRating.score}
              </div>
              <div className="flight-risk-dot-rating">
                {Array.from({ length: 5 }).map((_, dotIndex) => (
                  <span
                    key={dotIndex}
                    className={`flight-risk-dot ${
                      dotIndex < (confidenceRating.score ?? 0)
                        ? (confidenceStyles?.dotClass ?? "")
                        : "flight-risk-dot--empty"
                    }`}
                  />
                ))}
                <span className={`flight-risk-score-text ${confidenceStyles?.scoreTextClass ?? ""}`}>
                  {confidenceRating.score}/5
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Walmart AM */}
        <div className="flight-risk-card">
          <p className="flight-risk-card-label">{FlightRiskQuestions.WALMART_AM}</p>
          {isEditMode ? (
            <textarea
              className="fr-field-textarea"
              value={form.walmartAm}
              onChange={(e) => handleChange("walmartAm", e.target.value)}
              rows={3}
              placeholder="Yes / No / Partner name"
            />
          ) : (
            <span className={walmartAccountManager.className}>
              {walmartAccountManager.text}
            </span>
          )}
        </div>
      </div>

      {/* ── Row 2: Main issue + Contributing factor ─────────────────── */}
      <div className="flight-risk-grid-2">
        <div className="flight-risk-card">
          <p className="flight-risk-card-label">{FlightRiskQuestions.MAIN_ISSUE}</p>
          {isEditMode ? (
            <textarea
              className="fr-field-textarea"
              value={form.mainIssue}
              onChange={(e) => handleChange("mainIssue", e.target.value)}
              rows={3}
              placeholder="Describe the main issue…"
            />
          ) : flightRiskData?.mainIssueReasonForFlightRiskFlag?.trim() ? (
            <p className="flight-risk-card-value">
              {flightRiskData.mainIssueReasonForFlightRiskFlag}
            </p>
          ) : (
            <span className="flight-risk-fallback-dash">---</span>
          )}
        </div>

        <div className="flight-risk-card">
          <p className="flight-risk-card-label">{FlightRiskQuestions.CONTRIBUTING_FACTOR}</p>
          {isEditMode ? (
            <textarea
              className="fr-field-textarea"
              value={form.contributingFactor}
              onChange={(e) => handleChange("contributingFactor", e.target.value)}
              rows={3}
              placeholder="Contributing factor"
            />
          ) : flightRiskData?.whichOfTheFollowingFactorsPrimarilyContributedToTheFlightRisk?.trim() ? (
            <p className="flight-risk-card-value">
              {flightRiskData.whichOfTheFollowingFactorsPrimarilyContributedToTheFlightRisk}
            </p>
          ) : (
            <span className="flight-risk-fallback-dash">---</span>
          )}
        </div>
      </div>

      {/* ── Row 3: KPI + Sales + ROAS ─────────────────────────────────── */}
      <div className="flight-risk-grid-1-2">

        {/* KPI — free text in edit mode */}
        <div className="flight-risk-card">
          <p className="flight-risk-card-label">{FlightRiskQuestions.KPI}</p>
          {isEditMode ? (
            <textarea
              className="fr-field-textarea"
              value={form.kpi}
              onChange={(e) => handleChange("kpi", e.target.value)}
              rows={3}
              placeholder="e.g. Gross Merchandise Volume, ROAS"
            />
          ) : kpiDisplay ? (
            <p className="flight-risk-card-value">{kpiDisplay}</p>
          ) : (
            <span className="flight-risk-fallback-dash">---</span>
          )}
        </div>

        {/* Sales */}
        <div className="flight-risk-card">
          <p className="flight-risk-card-label">{FlightRiskQuestions.SALES}</p>
          {isEditMode ? (
            <input
              type="text"
              className="fr-field-input fr-field-input--grow fr-field-input--big-number"
              value={form.sales}
              onChange={(e) => handleChange("sales", e.target.value)}
              placeholder="e.g. 4079"
            />
          ) : salesMetric.isNull ? (
            <span className="flight-risk-fallback-dash">---</span>
          ) : salesMetric.isNumeric ? (
            <p className="flight-risk-big-number">{salesMetric.text}</p>
          ) : (
            <p className="flight-risk-card-value">{salesMetric.text}</p>
          )}
        </div>

        {/* ROAS */}
        <div className="flight-risk-card">
          <p className="flight-risk-card-label">{FlightRiskQuestions.ROAS}</p>
          {isEditMode ? (
            <input
              type="text"
              className="fr-field-input fr-field-input--grow fr-field-input--big-number"
              value={form.roas}
              onChange={(e) => handleChange("roas", e.target.value)}
              placeholder="e.g. 1.82"
            />
          ) : roasMetric.isNull ? (
            <span className="flight-risk-fallback-dash">---</span>
          ) : roasMetric.isNumeric ? (
            <p className="flight-risk-big-number">{roasMetric.text}</p>
          ) : (
            <p className="flight-risk-card-value">{roasMetric.text}</p>
          )}
        </div>
      </div>

      {/* ── Row 4: POA section ────────────────────────────────────────── */}
      <div className="flight-risk-poa-section">
        <div className="flight-risk-poa-header">
          {isEditMode ? (
            <div className="fr-poa-edit-header">
              <span className="flight-risk-poa-question">{FlightRiskQuestions.POA}</span>
              <input
                type="text"
                className="fr-field-input fr-poa-status-input"
                value={form.poaInPlace}
                onChange={(e) => handleChange("poaInPlace", e.target.value)}
                placeholder="Yes / No"
              />
            </div>
          ) : isPoaYesOrNo ? (
            <div className="flight-risk-poa-header-sub">
              <span className="flight-risk-poa-question">{FlightRiskQuestions.POA}</span>
              {hasPlanOfAction === "yes" ? (
                <span className="flight-risk-poa-status flight-risk-poa-status--yes">
                  <img src={GreenCheckBoxIcon} alt="" /> Yes
                </span>
              ) : (
                <span className="flight-risk-poa-status flight-risk-poa-status--no">
                  <img src={RedCrossBoxIcon} alt="" /> No
                </span>
              )}
            </div>
          ) : (
            <div className="flight-risk-poa-header-text">
              <span className="flight-risk-poa-question">{FlightRiskQuestions.POA}</span>
              <span className="flight-risk-poa-status-other">
                {hasPlanOfActionRaw || "---"}
              </span>
            </div>
          )}
        </div>

        {/* POA Recap */}
        {isEditMode ? (
          <div className="fr-poa-recap-edit">
            <p className="flight-risk-poa-recap-label">{FlightRiskQuestions.POA_RECAP}</p>
            <div className="flight-risk-poa-editor-wrap">
              <RichTextEditor
                content={form.poaRecap || ""}
                onContentUpdate={(documentHTMLContent) => {
                  const cleaned = documentHTMLContent
                    ? documentHTMLContent
                        .replace(/<p><\/p>/gi, "")
                        .replace(/<p><br\s*\/?>\s*<\/p>/gi, "")
                        .replace(/<br\s*\/?>/gi, "")
                        .trim()
                    : "";
                  handleChange("poaRecap", cleaned ? documentHTMLContent : "");
                }}
                isEditable={true}
                hideMenu={false}
                isActive={true}
                isDefaultHidingBorder={true}
                hideCopyButton={true}
                toolbarPosition="top"
                parentPage="ActionItems"
                activeUsers={activeUsers}
                placeholder="Enter plan of action details and next steps…"
              />
            </div>
          </div>
        ) : isHtmlPoaRecap && poaRecapRaw ? (
          <div className="flight-risk-poa-steps">
            <p className="flight-risk-poa-recap-label">{FlightRiskQuestions.POA_RECAP}</p>
            <div
              className="flight-risk-poa-html-content"
              dangerouslySetInnerHTML={{ __html: poaRecapRaw }}
            />
          </div>
        ) : planOfActionSteps.length > 0 ? (
          <div className="flight-risk-poa-steps">
            <p className="flight-risk-poa-recap-label">{FlightRiskQuestions.POA_RECAP}</p>
            {planOfActionSteps.map((stepDescription, stepIndex) => (
              <div key={stepIndex} className="flight-risk-poa-step">
                <span className="flight-risk-poa-badge">{stepIndex + 1}</span>
                <span className="flight-risk-poa-step-text">{stepDescription}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* ── Row 5: Bottom 3 cards ──────────────────────────────────────── */}
      <div className="flight-risk-grid-bottom">
        {/* Date of Last Significant Interaction */}
        <div className="flight-risk-card">
          <p className="flight-risk-card-label">
            {FlightRiskQuestions.DATE_OF_LAST_SIGNIFICANT_INTERACTION}
          </p>
          {isEditMode ? (
            <>
              <div
                className={`fr-field-date-trigger${isDatePickerOpen ? " fr-field-date-trigger--active" : ""}`}
                onClick={handleOpenDatePicker}
              >
                <div className="fr-field-date-trigger-left">
                  <span
                    className={
                      form.dateOfLastInteraction
                        ? "fr-field-date-value"
                        : "fr-field-date-placeholder"
                    }
                  >
                    {form.dateOfLastInteraction
                      ? formatDateWithSlash(form.dateOfLastInteraction)
                      : "Select a date"}
                  </span>
                </div>
                <div className="fr-field-date-icons">
                  {form.dateOfLastInteraction && (
                    <span
                      className="fr-field-date-clear"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDateChange(null);
                      }}
                      title="Clear date"
                    >
                      ✕
                    </span>
                  )}
                  <img
                    src={DueDateIcon}
                    alt="calendar"
                    className="fr-field-date-calendar-icon"
                  />
                </div>
              </div>
              {isDatePickerOpen && (
                <CommonDatePicker
                  value={form.dateOfLastInteraction || null}
                  anchorEl={datePickerAnchorEl}
                  onChange={handleDateChange}
                  onClose={handleCloseDatePicker}
                  minDate={effectiveMinDate}
                  maxDate={dayjs().endOf("day").toISOString()}
                  saveAsDateOnly={true}
                />
              )}
            </>
          ) : dateOfLastSignificantInteraction === "-" ? (
            <span className="flight-risk-fallback-dash">---</span>
          ) : (
            <p className="flight-risk-card-value">{dateOfLastSignificantInteraction}</p>
          )}
        </div>

        {/* Goals Moving Forward */}
        <div className="flight-risk-card">
          <p className="flight-risk-card-label">{FlightRiskQuestions.GOALS_MOVING_FORWARD}</p>
          {isEditMode ? (
            <textarea
              className="fr-field-textarea"
              value={form.goalsMovingForward}
              onChange={(e) => handleChange("goalsMovingForward", e.target.value)}
              rows={3}
              placeholder="Goals moving forward…"
            />
          ) : goalsMovingForward === "-" ? (
            <span className="flight-risk-fallback-dash">---</span>
          ) : (
            <p className="flight-risk-card-value">{goalsMovingForward}</p>
          )}
        </div>

        {/* People Involved — free text */}
        <div className="flight-risk-card">
          <p className="flight-risk-card-label">{FlightRiskQuestions.PEOPLE_INVOLVED}</p>
          {isEditMode ? (
            <textarea
              className="fr-field-textarea"
              value={form.peopleInvolved}
              onChange={(e) => handleChange("peopleInvolved", e.target.value)}
              rows={3}
              placeholder="Hub Leader, AM, PPC Manager…"
            />
          ) : peopleInvolved === "-" ? (
            <span className="flight-risk-fallback-dash">---</span>
          ) : (
            <p className="flight-risk-card-value">{peopleInvolved}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlightRiskAssessment;
